// nativePresence.ts — Läsnäolon natiivisilta (8.8.2026)
// Kytkee iOS:n geofence-valvonnan (SavrocPresencePlugin) päälle/pois
// operaattorin opt-in-asetuksen mukaan. Web/PWA:ssa kaikki on no-op —
// läsnäolo toimii vain natiivisovelluksessa.
import { registerPlugin } from '@capacitor/core';
import { authFetch } from '../api/client';
import { isNativeApp } from '../push/nativePush';

interface SavrocPresencePlugin {
  configure(opts: { latitude: number; longitude: number; radiusM: number; apiBase: string; token: string }): Promise<void>;
  requestPermissions(): Promise<{ status: string }>;
  getStatus(): Promise<{ configured: boolean; authorization: string }>;
  stop(): Promise<void>;
}

const Presence = registerPlugin<SavrocPresencePlugin>('SavrocPresence');

/** Absoluuttinen API-pohja natiiville (Swift ei tunne suhteellisia polkuja). */
function apiBase(): string {
  return `${window.location.origin}/api`;
}

/**
 * Kytke geofence-valvonta päälle: laitetoken + alue pluginille + lupapyyntö.
 * Kutsutaan asetuskytkimestä. Heittää virheen jos alue puuttuu.
 */
export async function enableNativePresence(): Promise<{ authorization: string }> {
  if (!isNativeApp()) return { authorization: 'web' };

  const meRes = await authFetch('/operator/presence/me');
  if (!meRes.ok) throw new Error('Läsnäoloasetusten haku epäonnistui');
  const me = await meRes.json();
  if (!me.geofence) throw new Error('Työpaikan aluetta ei ole vielä määritetty');

  const tokenRes = await authFetch('/operator/presence/device-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deviceInfo: navigator.userAgent }),
  });
  if (!tokenRes.ok) throw new Error('Laitetokenin luonti epäonnistui');
  const { token } = await tokenRes.json();

  await Presence.configure({
    latitude: me.geofence.latitude,
    longitude: me.geofence.longitude,
    radiusM: me.geofence.radiusM,
    apiBase: apiBase(),
    token,
  });
  const perm = await Presence.requestPermissions();
  return { authorization: perm.status };
}

/** Kytke pois: lopeta valvonta ja kumoa laitetokenit. */
export async function disableNativePresence(): Promise<void> {
  if (!isNativeApp()) return;
  try { await Presence.stop(); } catch { /* plugin puuttuu → ei mitään lopetettavaa */ }
  await authFetch('/operator/presence/device-token', { method: 'DELETE' }).catch(() => {});
}

/**
 * Itsekorjaus shellin mountissa: jos asetus on päällä mutta natiivivalvonta
 * ei ole konfiguroituna (uusi asennus, token kumottu), kytke uudelleen.
 * Hiljainen — ei koskaan heitä.
 */
export async function syncNativePresence(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const meRes = await authFetch('/operator/presence/me');
    if (!meRes.ok) return;
    const me = await meRes.json();
    if (!me.autoClockInEnabled || !me.geofence) return;
    const status = await Presence.getStatus();
    if (!status.configured) await enableNativePresence();
  } catch { /* hiljainen — yritetään seuraavassa mountissa */ }
}
